import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertService,
  AlertType,
  ColumnConfig,
  CustomAction,
  FieldType,
  GenericModalComponent,
  GenericTableComponent,
  GlobalAction,
  HeaderService,
  LoaderService,
  ModalFieldConfig,
  ModalMode,
  RowStyleConfig,
  TableAction,
  TableActionConfig,
} from '@solcre-org/core-ui';
import { finalize } from 'rxjs';
import { PortBerthingTypes } from '../../../shared/enums/port-berthing-types.enum';
import { PortTariffModel } from '../../../shared/models/port-tariff.model';
import { PortTariffDisplayService } from '../../../shared/services/port-tariff-display.service';
import { PortTariffService } from '../../../shared/services/port-tariff.service';
import { relevantBerthingTypesForCapacity } from '../../../shared/utils/port-berthing-types.utils';
import { ArchiveTariffFormModel } from './archive-tariff-form.model';
import { DuplicateTariffFormModel } from './duplicate-tariff-form.model';
import { PortTariffsContext } from './port-tariffs-context.model';
import { TariffFormModel } from './tariff-form.model';

type TariffFormMode = 'create' | 'edit';

@Component({
  selector: 'app-port-tariffs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GenericTableComponent,
    GenericModalComponent,
  ],
  templateUrl: './port-tariffs.html',
  styleUrls: ['./port-tariffs.css'],
})
export class PortTariffs implements OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerService = inject(HeaderService);
  private readonly loaderService = inject(LoaderService);
  private readonly alertService = inject(AlertService);
  private readonly tariffService = inject(PortTariffService);
  private readonly translateService = inject(TranslateService);
  private readonly displayService = inject(PortTariffDisplayService);

  statusCellTemplate = viewChild<TemplateRef<{ $implicit: PortTariffModel; column: ColumnConfig<PortTariffModel> }>>('statusCellTemplate');

  tariffs = signal<PortTariffModel[]>([]);
  selectedPortId = signal<string>('');
  selectedTariffId = signal<string>('');
  portContext = signal<PortTariffsContext>({
    portCode: '',
    portName: '',
    terminal: '',
    country: '',
    berthingType: '',
  });
  isLoading = signal(false);

  tariffFormOpen = signal(false);
  tariffFormMode = signal<TariffFormMode>('create');
  tariffModalMode = signal<ModalMode>(ModalMode.CREATE);
  archiveTariffOpen = signal(false);
  archiveTariffModalMode = signal<ModalMode>(ModalMode.CREATE);
  duplicateTariffOpen = signal(false);
  duplicateTariffModalMode = signal<ModalMode>(ModalMode.CREATE);

  tariffDraft: TariffFormModel = new TariffFormModel();
  // Plain property, not a signal — captured at modal-open time so a concurrent
  // list reload can't swap out which tariff is being archived mid-flow.
  archiveTariffTarget: PortTariffModel | null = null;
  archiveTariffDraft: ArchiveTariffFormModel = new ArchiveTariffFormModel();
  // Same rationale as archiveTariffTarget — pinned at modal-open time.
  duplicateTariffTarget: PortTariffModel | null = null;
  duplicateTariffDraft: DuplicateTariffFormModel = new DuplicateTariffFormModel();

  portHeaderTitle = computed(() => {
    const context = this.portContext();
    return context.portName || context.portCode || this.t('portTariffs.header.fallbackTitle', { portId: this.selectedPortId() || '' });
  });

  portHeaderMeta = computed(() => {
    const context = this.portContext();
    return [context.portCode, context.country, context.terminal].filter(Boolean).join(' · ') || this.t('portTariffs.header.fallbackMeta');
  });

  selectedTariff = computed(() => {
    const selectedId = this.selectedTariffId();
    return this.tariffs().find((tariff) => String(tariff.id) === String(selectedId)) ?? null;
  });

  // Both active and archived tariffs are always visible — archived rows stay as history.
  // Active rows sort first; within each group, alphabetically by berthing-type label.
  sortedTariffs = computed(() => {
    return [...this.tariffs()].sort((left, right) => {
      const leftRank = this.isActiveTariff(left) ? 0 : 1;
      const rightRank = this.isActiveTariff(right) ? 0 : 1;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return this.tariffTitle(left).localeCompare(this.tariffTitle(right)) || String(left.id ?? '').localeCompare(String(right.id ?? ''));
    });
  });

  activeTariffs = computed(() => this.tariffs().filter((tariff) => this.isActiveTariff(tariff)));
  archivedTariffs = computed(() => this.tariffs().filter((tariff) => !this.isActiveTariff(tariff)));

  tariffColumns = computed<ColumnConfig<PortTariffModel>[]>(() => [
    {
      label: this.t('portTariffs.table.status'),
      key: 'status',
      template: this.statusCellTemplate(),
    },
    {
      label: this.t('portTariffs.table.berthingType'),
      key: 'berthingType',
      format: (value) => this.displayService.berthingTypeLabel(value),
    },
    {
      label: this.t('portTariffs.table.lines'),
      key: 'lines',
      align: 'center',
      format: (_value, row) => String(this.activeLinesCount(row)),
    },
  ]);

  tariffRowStyleConfigs = computed<RowStyleConfig<PortTariffModel>[]>(() => [
    {
      condition: (row) => !this.isActiveTariff(row),
      className: 'port-tariffs-table__row--archived',
      priority: 1,
    },
  ]);

  tariffGlobalActions = computed<GlobalAction<PortTariffModel>[]>(() => [
    {
      action: 'backToPorts',
      label: this.t('portTariffs.actions.backToPorts'),
      icon: 'icon-arrow-left',
      callback: () => this.goBackToPorts(),
      class: 'c-btn c-btn--stroked',
    },
    {
      action: 'createTariff',
      label: this.t('portTariffs.actions.createTariff'),
      icon: 'icon-add',
      callback: () => this.openCreateTariff(),
      class: 'c-btn',
    },
  ]);

  tariffActions = computed<TableActionConfig<PortTariffModel>[]>(() => [
    {
      action: TableAction.VIEW,
      isExtra: false,
      icon: 'icon-arrow-right',
      tooltip: 'portTariffs.actions.viewTariff',
      tooltipPosition: 'top',
      callback: (tariff) => this.openTariffLines(tariff),
    },
    {
      action: TableAction.EDIT,
      isExtra: false,
      tooltip: 'portTariffs.actions.editTariff',
      tooltipPosition: 'top',
      shouldShow: (tariff) => this.isActiveTariff(tariff),
      callback: (tariff) => this.openEditTariff(tariff),
    },
  ]);

  tariffCustomActions = computed<CustomAction<PortTariffModel>[]>(() => [
    {
      title: this.t('portTariffs.actions.duplicateTariff'),
      icon: 'fa-copy',
      isExtra: false,
      tooltip: this.availableBerthingTypes().length
        ? 'portTariffs.actions.duplicateTariff'
        : 'portTariffs.messages.allBerthingTypesUsed',
      tooltipPosition: 'top',
      shouldDisable: () => !this.availableBerthingTypes().length,
      callback: (tariff) => this.openDuplicateTariff(tariff),
    },
    {
      title: this.t('portTariffs.actions.archiveTariff'),
      icon: 'fa-box-archive',
      class: 'context:error',
      isExtra: true,
      shouldShow: (tariff) => this.isActiveTariff(tariff),
      callback: (tariff) => this.openArchiveTariff(tariff),
    },
  ]);

  // A port's berthingType ('along_side' | 'med_moor' | 'both' | 'anchor' | 'third') describes what the
  // port supports. A tariff always applies to a single physical mooring mode, so a port marked 'both'
  // or 'third' needs one separate tariff per operative mode it covers — 'both'/'third' are never valid
  // tariff berthingType values themselves.
  relevantBerthingTypes = computed<PortBerthingTypes[]>(() => relevantBerthingTypesForCapacity(this.portContext().berthingType));

  // Only ACTIVE tariffs block a berthing type — an archived tariff frees the slot
  // so a new one can be created for the same port + berthingType combination.
  availableBerthingTypes = computed(() => {
    const usedActiveTypes = new Set(this.activeTariffs().map((tariff) => tariff.berthingType).filter(Boolean));
    return this.relevantBerthingTypes().filter((berthingType) => !usedActiveTypes.has(berthingType));
  });

  // CREATE shows only the still-available berthing types.
  // EDIT shows all relevant types so the user can keep or change the current one —
  // the existing tariff already occupies a slot, so we don't filter it out.
  tariffModalFields = computed<ModalFieldConfig<TariffFormModel>[]>(() => [
    {
      key: 'berthingType',
      label: this.t('portTariffs.fields.berthingType'),
      placeholder: this.t('portTariffs.fields.berthingTypePlaceholder'),
      type: FieldType.SELECT,
      readonly: false,
      searchable: true,
      options: this.tariffModalMode() === ModalMode.CREATE
        ? this.availableBerthingTypes().map((berthingType) => ({
            value: berthingType,
            label: this.displayService.berthingTypeLabel(berthingType),
          }))
        : this.relevantBerthingTypes().map((berthingType) => ({
            value: berthingType,
            label: this.displayService.berthingTypeLabel(berthingType),
          })),
      validators: [Validators.required],
      errorMessages: {
        required: this.t('validators.required'),
      },
    },
  ]);

  archiveTariffModalFields = computed<ModalFieldConfig<ArchiveTariffFormModel>[]>(() => [
    {
      key: 'reason',
      label: this.t('portTariffs.fields.reason'),
      placeholder: this.t('portTariffs.fields.reasonPlaceholder'),
      type: FieldType.TEXTAREA,
      readonly: false,
      validators: [Validators.required],
      errorMessages: {
        required: this.t('validators.required'),
      },
    },
  ]);

  // The duplicate always lands on a still-available berthing type — the source
  // tariff's own type is excluded since it's already occupied by an active tariff.
  duplicateTariffModalFields = computed<ModalFieldConfig<DuplicateTariffFormModel>[]>(() => [
    {
      key: 'berthingType',
      label: this.t('portTariffs.fields.berthingType'),
      placeholder: this.t('portTariffs.fields.berthingTypePlaceholder'),
      type: FieldType.SELECT,
      readonly: false,
      searchable: true,
      options: this.availableBerthingTypes().map((berthingType) => ({
        value: berthingType,
        label: this.displayService.berthingTypeLabel(berthingType),
      })),
      validators: [Validators.required],
      errorMessages: {
        required: this.t('validators.required'),
      },
    },
  ]);

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const portId = params.get('id') ?? '';
      this.selectedPortId.set(portId);
      this.selectedTariffId.set('');
      // Clear stale tariffs synchronously — otherwise the previous port's tariffs
      // stay in the signal until the fetch below resolves, and any computed that
      // combines tariffs with portContext (e.g. availableBerthingTypes) can briefly
      // mix data from two different ports.
      this.tariffs.set([]);
      this.updateHeaderConfig();
      this.reloadData();
    });

    // Port display metadata arrives as query params from the ports list — no extra GET needed.
    // paramMap and queryParamMap fire independently; the context may update after or before
    // the tariff list, so both handlers call updateHeaderConfig().
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.portContext.set({
        portCode: params.get('portCode') ?? '',
        portName: params.get('portName') ?? '',
        terminal: params.get('terminal') ?? '',
        country: params.get('country') ?? '',
        berthingType: params.get('berthingType') ?? '',
      });
      this.updateHeaderConfig();
    });
  }

  reloadData(): void {
    const portId = this.selectedPortId();
    if (!portId) {
      this.tariffs.set([]);
      return;
    }

    this.isLoading.set(true);
    this.loaderService.showLoader();
    this.tariffService.fetchPortTariffs(portId).pipe(
      finalize(() => {
        this.isLoading.set(false);
        this.loaderService.hideLoader();
      })
    ).subscribe({
      next: (tariffs) => {
        this.tariffs.set(tariffs ?? []);

        const selectedTariffId = this.resolveSelectedTariffId(tariffs ?? []);
        this.selectedTariffId.set(selectedTariffId);
        this.updateHeaderConfig();
      },
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  goBackToPorts(): void {
    this.router.navigate(['/app/ports']);
  }

  openTariffLines(tariff: PortTariffModel): void {
    if (!tariff?.id || !this.selectedPortId()) {
      return;
    }

    const context = this.portContext();
    this.router.navigate(['/app/ports', this.selectedPortId(), 'tariffs', tariff.id], {
      queryParams: {
        portCode: context.portCode,
        portName: context.portName,
        terminal: context.terminal,
        country: context.country,
        // Tariff's own berthingType takes precedence: a port marked 'both' has separate
        // tariffs (along_side + med_moor), so each should carry its specific type.
        berthingType: tariff.berthingType ?? context.berthingType,
        tariffId: tariff.id,
      },
    });
  }

  openTariff(tariff: PortTariffModel): void {
    if (!tariff?.id) {
      return;
    }

    this.selectedTariffId.set(String(tariff.id));
  }

  openCreateTariff(): void {
    if (!this.selectedPortId()) {
      return;
    }

    if (!this.availableBerthingTypes().length) {
      this.alertService.show({
        type: AlertType.DEFAULT,
        message: this.t('portTariffs.messages.allBerthingTypesUsed'),
        duration: 2500,
        inBottom: true,
      });
      return;
    }

    const berthingType =
      this.availableBerthingTypes()[0] ??
      this.relevantBerthingTypes()[0] ??
      PortBerthingTypes.ALONG_SIDE;

    this.tariffDraft = new TariffFormModel(berthingType);
    this.tariffFormMode.set('create');
    this.tariffModalMode.set(ModalMode.CREATE);
    this.tariffFormOpen.set(true);
  }

  openEditTariff(tariff: PortTariffModel): void {
    if (!tariff || !this.isActiveTariff(tariff)) {
      return;
    }

    this.tariffDraft = new TariffFormModel(tariff.berthingType ?? this.relevantBerthingTypes()[0] ?? PortBerthingTypes.ALONG_SIDE);
    this.selectedTariffId.set(String(tariff.id ?? ''));
    this.tariffFormMode.set('edit');
    this.tariffModalMode.set(ModalMode.EDIT);
    this.tariffFormOpen.set(true);
  }

  closeTariffForm(): void {
    this.tariffFormOpen.set(false);
  }

  onTariffModalSave(formValue: TariffFormModel): void {
    const portId = this.selectedPortId();
    if (!portId) {
      return;
    }

    const berthingType = String(formValue.berthingType ?? '').trim();
    if (!berthingType) {
      this.showMessage(this.t('portTariffs.messages.selectBerthingTypeBeforeSaving'), AlertType.DEFAULT);
      return;
    }

    const mode = this.tariffFormMode();
    if (mode === 'create') {
      this.loaderService.showLoader();
      this.tariffService.createPortTariff(String(portId), { berthingType }).pipe(
        finalize(() => this.loaderService.hideLoader())
      ).subscribe({
        next: (tariff) => {
          this.showMessage(this.t('portTariffs.messages.tariffCreated'), AlertType.SUCCESS);
          this.selectedTariffId.set(String(tariff.id ?? ''));
          this.tariffFormOpen.set(false);
          this.reloadData();
        },
        error: (error: HttpErrorResponse) => this.showError(error),
      });
      return;
    }

    const tariffId = this.selectedTariff()?.id;
    if (!tariffId) {
      return;
    }

    this.loaderService.showLoader();
    this.tariffService.updatePortTariff(String(tariffId), { berthingType }).pipe(
      finalize(() => this.loaderService.hideLoader())
    ).subscribe({
      next: () => {
        this.showMessage(this.t('portTariffs.messages.tariffUpdated'), AlertType.SUCCESS);
        this.tariffFormOpen.set(false);
        this.reloadData();
      },
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  openArchiveTariff(tariff: PortTariffModel): void {
    if (!tariff?.id) {
      return;
    }

    this.archiveTariffTarget = tariff;
    this.archiveTariffDraft = new ArchiveTariffFormModel('');
    this.archiveTariffModalMode.set(ModalMode.CREATE);
    this.archiveTariffOpen.set(true);
  }

  closeArchiveTariff(): void {
    this.archiveTariffOpen.set(false);
    this.archiveTariffTarget = null;
    this.archiveTariffDraft = new ArchiveTariffFormModel('');
  }

  onArchiveTariffModalSave(formValue: ArchiveTariffFormModel): void {
    const target = this.archiveTariffTarget;
    const reason = String(formValue.reason ?? '').trim();

    if (!target?.id) {
      return;
    }

    if (!reason) {
      this.showMessage(this.t('portTariffs.messages.archiveReasonRequired'), AlertType.DEFAULT);
      return;
    }

    this.loaderService.showLoader();
    this.tariffService.archivePortTariff(String(target.id), reason).pipe(
      finalize(() => this.loaderService.hideLoader())
    ).subscribe({
      next: () => {
        this.showMessage(this.t('portTariffs.messages.tariffArchived'), AlertType.SUCCESS);
        this.closeArchiveTariff();
        this.reloadData();
      },
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  openDuplicateTariff(tariff: PortTariffModel): void {
    if (!tariff?.id || !this.availableBerthingTypes().length) {
      return;
    }

    this.duplicateTariffTarget = tariff;
    this.duplicateTariffDraft = new DuplicateTariffFormModel(this.availableBerthingTypes()[0] ?? null);
    this.duplicateTariffModalMode.set(ModalMode.CREATE);
    this.duplicateTariffOpen.set(true);
  }

  closeDuplicateTariff(): void {
    this.duplicateTariffOpen.set(false);
    this.duplicateTariffTarget = null;
    this.duplicateTariffDraft = new DuplicateTariffFormModel();
  }

  onDuplicateTariffModalSave(formValue: DuplicateTariffFormModel): void {
    const target = this.duplicateTariffTarget;
    const berthingType = String(formValue.berthingType ?? '').trim();

    if (!target?.id) {
      return;
    }

    if (!berthingType) {
      this.showMessage(this.t('portTariffs.messages.selectBerthingTypeBeforeSaving'), AlertType.DEFAULT);
      return;
    }

    this.loaderService.showLoader();
    this.tariffService.duplicatePortTariff(String(target.id), berthingType).pipe(
      finalize(() => this.loaderService.hideLoader())
    ).subscribe({
      next: (tariff) => {
        this.showMessage(this.t('portTariffs.messages.tariffDuplicated'), AlertType.SUCCESS);
        this.selectedTariffId.set(String(tariff.id ?? ''));
        this.closeDuplicateTariff();
        this.reloadData();
      },
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  tariffTitle(tariff: PortTariffModel): string {
    return this.displayService.berthingTypeLabel(tariff.berthingType);
  }

  isActiveTariff(tariff?: PortTariffModel | null): boolean {
    return !!tariff && tariff.isActive !== false && !tariff.archivedAt;
  }

  activeLinesCount(tariff: PortTariffModel): number {
    return (tariff.lines ?? []).filter((line) => line.isActive !== false && !line.archivedAt).length;
  }

  // Keeps the UI selection stable across reloads (e.g., after create/edit/archive).
  // Falls back to first active tariff, then first item of any status if nothing else matches.
  private resolveSelectedTariffId(tariffs: PortTariffModel[]): string {
    const currentSelectedId = this.selectedTariffId();
    const currentSelectedExists = tariffs.some((tariff) => String(tariff.id) === String(currentSelectedId));
    if (currentSelectedExists) {
      return currentSelectedId;
    }

    const firstActive = tariffs.find((tariff) => this.isActiveTariff(tariff));
    if (firstActive?.id) {
      return String(firstActive.id);
    }

    return String(tariffs[0]?.id ?? '');
  }

  private updateHeaderConfig(): void {
    this.headerService.setHeaderConfig({
      title: this.portHeaderTitle(),
      text: this.portHeaderMeta(),
      visible: true,
      showDefaultFilter: false,
      showDefaultCreate: false,
      customActions: [],
    });
  }

  private showMessage(message: string, type: AlertType): void {
    this.alertService.show({
      type,
      message,
      duration: 2500,
      inBottom: true,
    });
  }

  private showError(error: HttpErrorResponse): void {
    const payload = error?.error;
    const detail =
      payload?.detail ??
      payload?.error ??
      payload?.message ??
      (Array.isArray(payload?.violations)
        ? payload.violations.map((violation: any) => violation?.message).filter(Boolean).join(' · ')
      : null) ??
      this.t('errors.generic');

    this.showMessage(detail, AlertType.ERROR);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }

  ngOnDestroy(): void {
    this.headerService.clearCustomActions();
    this.headerService.clearHeaderData();
  }
}
